/* eslint-disable react-hooks/exhaustive-deps */
import { Column } from "@ant-design/plots";
import { DatePicker } from "antd";
import axios from "axios";
import dayjs from "dayjs";
import { useEffect, useState } from "react";

import { cpSheetDailyAnalyticsBiGraphConfig } from "./helper";
import { useGraphTheme } from "../../../../hooks/useGraphTheme";
import { showErrorNotification } from "../../../../utils/notification";
import { clamp } from "../../../../utils/responsiveHelpers";
import AnalyticsGenericBarGraph from "../../../common-utils/GenericBarGraph";

const CPSheetGeneralAnalyticsDash = ({ ROOT_URL }) => {
  const graphTheme = useGraphTheme();
  const BASE_BACKEND_URL = "admin/cp-sheet/analytics/general";

  const currentDate = new Date();
  const oneMonthAgo = new Date(
    currentDate.getFullYear(),
    currentDate.getMonth() - 1,
    currentDate.getDate()
  );

  // Filter States.
  const [filter, setFilter] = useState({
    startDate: oneMonthAgo,
    endDate: currentDate
  });

  // Page states.
  const [dailyVisitGraphPoints, setDailyVisitGraphPoints] = useState([]);
  const [dailySolvedCountData, setDailySolvedCountData] = useState([]);
  const [dailySolvedUserData, setDailySolvedUserData] = useState([]);
  const [dailyUserVisitCfConnectCount, setDailyUserVisitCfConnectCount] = useState([]);

  const fetchDateWiseVisitGraphPoints = () => {
    axios
      .get(`${ROOT_URL}/${BASE_BACKEND_URL}/daily-visit-graph-points`, {
        params: {
          filter
        }
      })
      .then((res) => {
        setDailyVisitGraphPoints(res.data);
      })
      .catch((err) => {
        showErrorNotification(err);
      });
  };

  const fetchDailySolvedCountData = () => {
    axios
      .get(`${ROOT_URL}/${BASE_BACKEND_URL}/daily-solved-problem-data`, {
        params: {
          filter
        }
      })
      .then((res) => {
        const { graphPoints = [] } = res.data;
        setDailySolvedCountData(graphPoints);
      })
      .catch((err) => {
        showErrorNotification(err);
      });
  };

  const fetchDailySolvedUserData = () => {
    axios
      .get(`${ROOT_URL}/${BASE_BACKEND_URL}/daily-solved-user-count`, {
        params: {
          filter
        }
      })
      .then((res) => {
        const { graphPoints = [] } = res.data;
        setDailySolvedUserData(graphPoints);
      })
      .catch((err) => {
        showErrorNotification(err);
      });
  };

  const fetchDailyVisitCfConnectData = () => {
    axios
      .get(`${ROOT_URL}/${BASE_BACKEND_URL}/visitor-cf-stats`, {
        params: {
          filter
        }
      })
      .then((res) => {
        const { graphPoints = [] } = res.data;
        setDailyUserVisitCfConnectCount(graphPoints);
      })
      .catch((err) => {
        showErrorNotification(err);
      });
  };

  useEffect(() => {
    fetchDateWiseVisitGraphPoints();
    fetchDailySolvedCountData();
    fetchDailySolvedUserData();
    fetchDailyVisitCfConnectData();
  }, [filter]);

  return (
    <div
      style={{
        marginTop: "20px",
        marginLeft: clamp(12, 30),
        marginRight: clamp(12, 30)
      }}
    >
      <div
        style={{
          marginBottom: "10px",
          display: "flex",
          flexDirection: "row",
          flexWrap: "wrap",
          gap: "5px",
          minHeight: "30px"
        }}
      >
        Date filter:{" "}
        <DatePicker.RangePicker
          style={{ width: "20%" }}
          value={
            filter.startDate && filter.endDate
              ? [dayjs(filter.startDate), dayjs(filter.endDate)]
              : []
          }
          onChange={(dates) => {
            if (dates) {
              setFilter((prevFilter) => ({
                ...prevFilter,
                startDate: dates[0].startOf("day").toDate(),
                endDate: dates[1].endOf("day").toDate()
              }));
            } else {
              setFilter((prevFilter) => ({
                ...prevFilter,
                startDate: null,
                endDate: null
              }));
            }
          }}
        />
      </div>

      <h3>Daily Visit Logged-in users Count</h3>
      <p>
        This graph shows the total visits on CP sheet aggregated on date basis. This visit count
        contains events like page loads and refreshes for <b>logged-in users.</b>
      </p>
      <div
        style={{
          marginBottom: "10px",
          display: "flex",
          flexDirection: "column",
          gap: "5px"
        }}
      >
        <AnalyticsGenericBarGraph
          dataPoints={dailyVisitGraphPoints?.loggedInTotalVisitsGraphPoints}
        />
      </div>

      <h3>Unique daily logged-in users Visit Count</h3>
      <p>
        This graph shows the unique user visits on CP sheet aggregated on date basis. Even if one
        user visits the Page 100s of times in a day, all the events are normalized to 1 per each
        user.
      </p>
      <div
        style={{
          marginBottom: "10px",
          display: "flex",
          flexDirection: "column",
          gap: "5px"
        }}
      >
        <AnalyticsGenericBarGraph
          dataPoints={dailyVisitGraphPoints?.loggedInUniqueVisitsGraphPoints}
        />
      </div>

      <h3>Daily Visit non-logged-in users Count</h3>
      <p>
        This graph shows the total visits on CP sheet aggregated on date basis. This visit count
        contains events like page loads and refreshes for <b>non-logged-in users.</b>
      </p>
      <div
        style={{
          marginBottom: "10px",
          display: "flex",
          flexDirection: "column",
          gap: "5px"
        }}
      >
        <AnalyticsGenericBarGraph
          dataPoints={dailyVisitGraphPoints?.nonLoggedInTotalVisitsGraphPoints}
        />
      </div>

      <h3>Cf Connected Data {"<>"} Visit Count </h3>
      <p>
        This graph shows the division of unique user visits b/w <code>CF connected</code> and{" "}
        <code>non-CF connected</code>.{" "}
        <b>
          <code>CF connected</code>
        </b>{" "}
        means those users which have verified their CF handle and{" "}
        <b>
          <code>non-CF connected</code>
        </b>{" "}
        contains all those users who are using the sheet either without any CF handle or with some
        non-verified handle.
      </p>
      <div
        style={{
          marginBottom: "10px",
          display: "flex",
          flexDirection: "column",
          gap: "5px"
        }}
      >
        <Column
          {...cpSheetDailyAnalyticsBiGraphConfig(dailyUserVisitCfConnectCount)}
          {...graphTheme}
        />
      </div>

      <h3>Daily Verified submission Count Data</h3>
      <h4>
        Data before 8th Feb 2025 is not available because it is not correct due to a code bug.
      </h4>
      <p>
        This graph shows the total number of verified submissions from <b>CF Verified</b> users on
        each day in the date range. Entries with greater than{" "}
        <b>greater than 15 submissions per user</b> on a single day are removed from the
        calculations to minimize data skew because <b>submissions greater than 15</b> usually
        contain entries of users who have synced submissions for the first time.
      </p>
      <div
        style={{
          marginBottom: "10px",
          display: "flex",
          flexDirection: "column",
          gap: "5px"
        }}
      >
        <AnalyticsGenericBarGraph dataPoints={dailySolvedCountData} />
      </div>

      <h3>User count who solved more than 1 problem</h3>
      <h4>
        Data before 8th Feb 2025 is not available because it is not correct due to a code bug.
      </h4>
      <p>
        This graph shows the total number of <b>CF Verified</b> users on each day in the date range
        who have solved at-least 1 problem. Entries with <b>greater than 15 submissions per user</b>{" "}
        on a single day are removed from the calculations to minimize and keep the above graph in
        sync with the average graph and removing extreme outliers will give us a better view of
        daily usage.
      </p>
      <div
        style={{
          marginBottom: "10px",
          display: "flex",
          flexDirection: "column",
          gap: "5px"
        }}
      >
        <AnalyticsGenericBarGraph dataPoints={dailySolvedUserData} />
      </div>
    </div>
  );
};

export default CPSheetGeneralAnalyticsDash;
